import { Alert, Box, Button, Dialog, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cameraErrorMessage } from './BarcodeScanner';
import { standaloneFieldSx } from '../../common/fieldStyles';

// Formatos aceitos (nomes da API BarcodeDetector; o zxing usa o mesmo conjunto).
const DETECTOR_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code'];
const ZXING_FORMATS = ['EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'CODE_128', 'CODE_39', 'ITF', 'QR_CODE'];

// Dígito verificador GS1 (EAN-13, EAN-8, UPC-A): soma ponderada 3/1 da direita p/ esquerda.
function hasValidCheckDigit(code) {
    if (!/^\d+$/.test(code)) return false;
    const digits = code.split('').map(Number);
    const check = digits.pop();
    const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === check;
}

// Aceita ou rejeita uma leitura bruta. Formatos GS1 exigem tamanho e dígito corretos.
function isValidReading(value, format) {
    if (!value || !String(value).trim()) return false;
    const fmt = String(format || '').toLowerCase();
    if (fmt === 'ean_13') return value.length === 13 && hasValidCheckDigit(value);
    if (fmt === 'ean_8') return value.length === 8 && hasValidCheckDigit(value);
    if (fmt === 'upc_a') return value.length === 12 && hasValidCheckDigit(value);
    return true;
}

const SCAN_HINT = 'Aponte a câmera para o código de barras.';

// Leitor de código de barras reutilizável em tela cheia (Dialog).
// Props: open, onClose, onDetected(code, format) — chamado uma única vez por abertura
// e já após fechar a câmera; o pai decide o que fazer (preencher campo, toast...).
// Usa BarcodeDetector nativo quando disponível e @zxing (carregado sob demanda) como fallback.
export default function BarcodeScannerDialog({ open, onClose, onDetected, title = 'Ler código de barras' }) {
    const [video, setVideo] = useState(null);
    const [error, setError] = useState('');
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const [photoBusy, setPhotoBusy] = useState(false);
    const [photoMessage, setPhotoMessage] = useState('');
    const doneRef = useRef(false);
    const onDetectedRef = useRef(onDetected);
    onDetectedRef.current = onDetected;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const fileInputRef = useRef(null);

    const finish = useCallback((value, format) => {
        if (doneRef.current) return;
        doneRef.current = true;
        try { if (navigator.vibrate) navigator.vibrate(80); } catch (_) { /* ignore */ }
        onCloseRef.current();
        onDetectedRef.current(value, format);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        doneRef.current = false;
        setError('');
        setTorchSupported(false);
        setTorchOn(false);
        setManualValue('');
        setPhotoMessage('');
        return undefined;
    }, [open]);

    useEffect(() => {
        if (!open || !video) return undefined;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setError('A câmera só funciona em conexão segura (HTTPS) e em navegadores compatíveis. Você pode digitar o código manualmente.');
            return undefined;
        }

        let stopped = false;
        let timer;
        let controls;
        let stream;

        const cleanup = () => {
            stopped = true;
            clearInterval(timer);
            if (controls) {
                try { controls.stop(); } catch (_) { /* já parado */ }
                controls = null;
            }
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
                stream = null;
            }
            if (video.srcObject) {
                try { video.srcObject.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
                video.srcObject = null;
            }
        };

        const detectTorch = () => {
            const track = video.srcObject && video.srcObject.getVideoTracks && video.srcObject.getVideoTracks()[0];
            const caps = track && track.getCapabilities ? track.getCapabilities() : null;
            if (!stopped && caps && caps.torch) setTorchSupported(true);
        };

        const openCamera = async () => {
            try {
                return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
            } catch (e) {
                if (e && e.name === 'OverconstrainedError') {
                    return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                }
                throw e;
            }
        };

        const startNative = async (detector) => {
            stream = await openCamera();
            if (stopped) { cleanup(); return; }
            video.setAttribute('playsinline', '');
            video.muted = true;
            video.srcObject = stream;
            await video.play();
            detectTorch();
            timer = setInterval(async () => {
                try {
                    const codes = await detector.detect(video);
                    const hit = codes.find((c) => isValidReading(c.rawValue, c.format));
                    if (hit) finish(hit.rawValue, hit.format);
                } catch (_) {
                    // frame sem leitura: tenta no próximo ciclo
                }
            }, 250);
        };

        const startZxing = async () => {
            const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
                import('@zxing/browser'),
                import('@zxing/library')
            ]);
            if (stopped) return;
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS.map((f) => BarcodeFormat[f]));
            hints.set(DecodeHintType.TRY_HARDER, true);
            const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
            video.setAttribute('playsinline', '');
            video.muted = true;
            const onResult = (result) => {
                if (!result) return;
                const format = BarcodeFormat[result.getBarcodeFormat()];
                if (isValidReading(result.getText(), format)) finish(result.getText(), format);
            };
            let c;
            try {
                c = await reader.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } }, audio: false }, video, onResult);
            } catch (e) {
                if (e && e.name === 'OverconstrainedError' && !stopped) {
                    c = await reader.decodeFromConstraints({ video: true, audio: false }, video, onResult);
                } else {
                    throw e;
                }
            }
            if (stopped) { c.stop(); return; }
            controls = c;
            detectTorch();
        };

        const start = async () => {
            let detector = null;
            if (typeof window.BarcodeDetector !== 'undefined') {
                try { detector = new window.BarcodeDetector({ formats: DETECTOR_FORMATS }); } catch (_) { detector = null; }
            }
            try {
                if (detector) await startNative(detector);
                else await startZxing();
            } catch (e) {
                if (stopped) return;
                cleanup();
                setError(cameraErrorMessage(e));
            }
        };
        start();
        return cleanup;
    }, [open, video, finish]);

    const toggleTorch = async () => {
        const track = video && video.srcObject && video.srcObject.getVideoTracks()[0];
        if (!track) return;
        try {
            await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
            setTorchOn((v) => !v);
        } catch (_) {
            setTorchSupported(false);
        }
    };

    const handleManual = () => {
        const value = manualValue.trim();
        if (value) finish(value, 'manual');
    };

    const handlePhoto = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file) return;
        setPhotoBusy(true);
        setPhotoMessage('');
        const url = URL.createObjectURL(file);
        try {
            const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
                import('@zxing/browser'),
                import('@zxing/library')
            ]);
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS.map((f) => BarcodeFormat[f]));
            hints.set(DecodeHintType.TRY_HARDER, true);
            const result = await new BrowserMultiFormatReader(hints).decodeFromImageUrl(url);
            const format = BarcodeFormat[result.getBarcodeFormat()];
            if (isValidReading(result.getText(), format)) {
                finish(result.getText(), format);
            } else {
                setPhotoMessage('Código lido é inválido. Tire outra foto, mais nítida e de perto.');
            }
        } catch (_) {
            setPhotoMessage('Não foi possível ler o código na foto. Tente outra foto, mais nítida e de perto.');
        } finally {
            URL.revokeObjectURL(url);
            setPhotoBusy(false);
        }
    };

    const safeTop = 'env(safe-area-inset-top, 0px)';
    const safeBottom = 'env(safe-area-inset-bottom, 0px)';

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={() => onClose()}
            sx={{ zIndex: 1600 }}
            PaperProps={{ sx: { backgroundColor: '#000', color: '#fff', backgroundImage: 'none' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pt: safeTop, pl: 'calc(8px + env(safe-area-inset-left, 0px))', pr: 'calc(8px + env(safe-area-inset-right, 0px))', minHeight: `calc(56px + ${safeTop})`, boxSizing: 'border-box' }}>
                <Typography noWrap sx={{ flex: 1, ml: 1, fontSize: 20, fontWeight: 500, color: '#fff' }}>{title}</Typography>
                {torchSupported && !error && (
                    <IconButton aria-label={torchOn ? 'Desligar lanterna' : 'Ligar lanterna'} onClick={toggleTorch} sx={{ color: torchOn ? '#FFD54F' : '#fff', width: 44, height: 44 }}>
                        {torchOn ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
                    </IconButton>
                )}
                <IconButton aria-label="Fechar leitor" onClick={() => onClose()} sx={{ color: '#fff', width: 44, height: 44 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, backgroundColor: '#000' }}>
                {!error && (
                    <>
                        <Box component="video" ref={setVideo} playsInline muted autoPlay sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                        {/* moldura-guia de leitura */}
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Box sx={{ width: 'min(80vw, 420px)', height: 'min(36vw, 180px)', minHeight: 110, border: '3px solid #448AFF', borderRadius: '16px', boxShadow: '0 0 0 100vmax rgba(0,0,0,0.45)' }} />
                        </Box>
                    </>
                )}
            </Box>

            <Box sx={{ flexShrink: 0, px: 2, pt: 1.5, pb: `calc(12px + ${safeBottom})`, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {error ? (
                    <>
                        <Alert severity="warning">{error}</Alert>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                value={manualValue}
                                onChange={(e) => setManualValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleManual(); }}
                                placeholder="Digite o código"
                                fullWidth
                                variant="outlined"
                                inputProps={{ inputMode: 'text', autoComplete: 'off', 'aria-label': 'Código digitado manualmente' }}
                                sx={standaloneFieldSx}
                            />
                            <Button variant="contained" onClick={handleManual} disabled={!manualValue.trim()} sx={{ minWidth: 88, height: 48, textTransform: 'none', fontWeight: 700 }}>
                                Usar
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography sx={{ fontSize: 14, opacity: 0.8, textAlign: 'center' }}>{SCAN_HINT}</Typography>
                )}
                {photoMessage && <Alert severity="info">{photoMessage}</Alert>}
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePhoto} />
                <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    disabled={photoBusy}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    sx={{ height: 44, textTransform: 'none', fontWeight: 600, color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
                >
                    {photoBusy ? 'Lendo foto...' : 'Ler a partir de uma foto'}
                </Button>
            </Box>
        </Dialog>
    );
}
